import { escapeHtmlContent } from "@src/utils/diffUtils";
import { Marked, Renderer } from "marked";

/**
 * Underline extension for `marked` that mirrors @tiptap/extension-underline's
 * markdown serializer. StarterKit v3.19+ bundles Underline, and TipTap's
 * getMarkdown() serializes underlined text as `++text++`.
 *
 * Includes both a tokenizer (for lexing) and a renderer (for HTML output),
 * so the extension works with both `marked.lexer()` and `marked.parse()`.
 */
const underlineExtension = {
  name: "underline",
  level: "inline",
  start(src) {
    return src.indexOf("++");
  },
  tokenizer(src) {
    const match = /^\+\+([\s\S]+?)\+\+/.exec(src);
    if (!match) return undefined;
    return {
      type: "underline",
      raw: match[0],
      text: match[1].trim(),
      tokens: this.lexer.inlineTokens(match[1].trim()),
    };
  },
  childTokens: ["tokens"],
  renderer(token) {
    return `<u>${this.parser.parseInline(token.tokens ?? [])}</u>`;
  },
};

// Tiptap serializes overlapping bold/italic with these tags, and table-cell line breaks as <br>.
const FORMATTING_TAG = /^<(\/?)(em|strong)>$/;

function markFormattingTags(inlineTokens, inTableCell = false) {
  const open = { em: [], strong: [] };
  const visit = (tokens) =>
    tokens.forEach((token) => {
      if (token.type === "html") {
        if (inTableCell && token.text === "<br>")
          token.formatting = true;
        const [, closing, tag] =
          FORMATTING_TAG.exec(token.text) ?? [];
        if (tag && !closing) open[tag].push(token);
        const opener = tag && closing && open[tag].pop();
        if (opener) opener.formatting = token.formatting = true;
      }
      visit(token.tokens ?? []);
    });
  visit(inlineTokens);
}

/** Flags the HTML tokens that are Tiptap formatting; every other HTML token is literal text. */
export function markFormattingHtml(token) {
  if (token.type === "table") {
    for (const cell of [...token.header, ...token.rows.flat()]) {
      markFormattingTags(cell.tokens, true);
    }
  } else if (
    ["paragraph", "heading", "text"].includes(token.type) &&
    token.tokens
  ) {
    markFormattingTags(token.tokens);
  }
}

const TABLE_DELIMITER_ROW = /^\|(\s*:?-+:?\s*\|)+\s*$/;

const isTableStart = (lines, i) =>
  lines[i].startsWith("|") &&
  TABLE_DELIMITER_ROW.test(lines[i + 1] ?? "");

function endsWithTable(segment) {
  const lines = segment.split("\n");
  let start = lines.length;
  while (start > 0 && lines[start - 1].startsWith("|")) start--;
  return lines
    .slice(start + 1)
    .some((line) => TABLE_DELIMITER_ROW.test(line));
}

/** Expands single-`\n` paragraph breaks to `\n\n`, keeping code fences and tables on consecutive lines. */
function expandNewlines(text) {
  const lines = text.split("\n");
  const segments = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const fence = /^(```|~~~)/.exec(line)?.[1];

    if (fence) {
      const codeLines = [line];
      i++;

      while (i < lines.length) {
        codeLines.push(lines[i]);
        if (lines[i].startsWith(fence)) {
          break;
        }
        i++;
      }

      segments.push(codeLines.join("\n"));
    } else if (isTableStart(lines, i)) {
      const tableLines = [line];
      while (i + 1 < lines.length && lines[i + 1].startsWith("|")) {
        i++;
        tableLines.push(lines[i]);
      }
      segments.push(tableLines.join("\n"));
    } else {
      segments.push(line);
    }
    i++;
  }

  return segments.join("\n\n");
}

/**
 * Splits stored text into markdown segments (strings) and blank-line counts
 * (numbers). Tiptap pads tables with an extra newline, stored as one extra
 * blank line beside them.
 */
export function splitStoredMarkdown(text) {
  const parts = text.split(/(\n{2,})/);
  return parts.map((part, i) => {
    if (i % 2 === 0) return expandNewlines(part);
    const besideTable =
      endsWithTable(parts[i - 1]) ||
      isTableStart(parts[i + 1].split("\n"), 0);
    return part.length - 1 - Number(besideTable);
  });
}

// ProseMirror collapses plain spaces; stripNbsp restores them on save.
const keepSpaces = (render) =>
  function (token) {
    return render
      .call(this, token)
      .replace(
        /^(<p>)( +)| {2,}/g,
        (match, p = "", spaces = match) =>
          p + "\u00A0".repeat(spaces.length),
      );
  };

/**
 * Create a private `marked` instance with our custom extensions.
 *
 * We must NOT use the global `marked` singleton because `@tiptap/markdown`
 * registers custom tokenizers (e.g. "underline") on it via `marked.use()`.
 * Those tokenizers produce token types that have no corresponding renderer
 * in the global instance, causing `marked.parse()` to throw.
 *
 * A private instance is immune to that global pollution.
 */
export function createLocalMarked() {
  const instance = new Marked();
  instance.use({
    extensions: [underlineExtension],
    // Stored notes are raw text: no HTML blocks, and indentation never means code.
    tokenizer: { html: () => undefined, code: () => undefined },
    walkTokens: markFormattingHtml,
    renderer: {
      html: ({ text, formatting }) =>
        formatting ? text : escapeHtmlContent(text),
      paragraph: keepSpaces(Renderer.prototype.paragraph),
      heading: keepSpaces(Renderer.prototype.heading),
      listitem: keepSpaces(Renderer.prototype.listitem),
      // Tiptap keeps marked's trailing newline as an empty last line.
      code: (token) =>
        Renderer.prototype
          .code(token)
          .replace("\n</code>", "</code>"),
    },
  });
  return instance;
}
